import React, { useEffect, useMemo, useState, useCallback } from 'react';
import logo from "../assets/logo.png";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { supabase } from '../supabaseClient';
import { faSun, faMoon } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { isDemoCompanyInstituteRow } from '../demoCompanyLeaderboard';
import { useAuth } from '../AuthContext';
import { buildDemoCollegeLeaderboard, isDemoCollegeLeaderboard } from '../demoCollegeLeaderboard';
import { isPaddedUcdLogo } from '../institutionLogoPresentation';

/* ═══════════════════════════════════════════
   RESPONSIVE HOOK
═══════════════════════════════════════════ */
const useWindowWidth = () => {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
};

/* ═══════════════════════════════════════════
   THEME
═══════════════════════════════════════════ */
const DARK = {
  mode: "dark", accent: "#0C69C8", accentDark: "#0A1D44", accentLight: "#5BA8F7",
  accentGrad: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow: "rgba(12,105,200,0.35)", accentBgSubtle: "rgba(12,105,200,0.08)",
  accentBgLight: "rgba(12,105,200,0.14)", accentBorder: "rgba(12,105,200,0.25)",
  bg: "#0A0A0A", bgAlt: "#111111", bgCard: "#141414", bgElevated: "#1E1E1E",
  border: "rgba(255,255,255,0.08)", borderMedium: "rgba(255,255,255,0.12)",
  shadowSm: "0 1px 3px rgba(0,0,0,0.4)", shadowMd: "0 4px 16px rgba(0,0,0,0.5)",
  shadowLg: "0 12px 40px rgba(0,0,0,0.6)",
  text: "#FAFAFA", textSecondary: "rgba(255,255,255,0.68)", textMuted: "rgba(255,255,255,0.38)",
  textOnAccent: "#FFFFFF", headerBg: "rgba(10,10,10,0.92)",
  inputBg: "rgba(255,255,255,0.05)", inputBorder: "rgba(255,255,255,0.10)", inputText: "#FFFFFF",
  gold: "#D4A017", silver: "#A0AEC0", bronze: "#C97B3A",
  success: "#34D399", danger: "#EF4444", warning: "#F59E0B", scrollbarThumb: "rgba(255,255,255,0.12)",
};
const LIGHT = {
  mode: "light", accent: "#0C69C8", accentDark: "#0A1D44", accentLight: "#0C69C8",
  accentGrad: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow: "rgba(12,105,200,0.25)", accentBgSubtle: "rgba(12,105,200,0.05)",
  accentBgLight: "rgba(12,105,200,0.08)", accentBorder: "rgba(12,105,200,0.18)",
  bg: "#F5F7FA", bgAlt: "#EDF0F5", bgCard: "#FFFFFF", bgElevated: "#FFFFFF",
  border: "rgba(10,29,68,0.08)", borderMedium: "rgba(10,29,68,0.14)",
  shadowSm: "0 1px 3px rgba(10,29,68,0.06)", shadowMd: "0 4px 16px rgba(10,29,68,0.09)",
  shadowLg: "0 12px 40px rgba(10,29,68,0.12)",
  text: "#0F1629", textSecondary: "#3D4A6B", textMuted: "#7A87A8",
  textOnAccent: "#FFFFFF", headerBg: "rgba(245,247,250,0.95)",
  inputBg: "#F8F9FC", inputBorder: "rgba(10,29,68,0.12)", inputText: "#0F1629",
  gold: "#B8860B", silver: "#718096", bronze: "#A0522D",
  success: "#059669", danger: "#DC2626", warning: "#D97706", scrollbarThumb: "rgba(10,29,68,0.12)",
};

/* ═══════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════ */
const templateIcon = (type) => {
  switch (type) {
    case 'School': return '🎒'; case 'College': return '🎓'; case 'Company': return '🏢';
    case 'Club': return '🏅'; case 'Gym': return '🏋️'; case 'Organisation': return '🏛️'; case 'Challenge': return '🏆';
    case 'Collective Group': return '🤝'; default: return '🏫';
  }
};
const InstituteImage = ({ C, institute, style = {}, iconSize = 20, fit = "cover" }) => institute.image_url ? (
  isPaddedUcdLogo(institute.image_url) && fit === 'contain'
    ? <span style={{ background: C.bgCard, ...style, display: 'inline-grid', placeItems: 'center', overflow: 'hidden', padding: 0 }}><img src={institute.image_url} alt={institute.name} loading="lazy" style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', transform: 'scale(1.24)' }} /></span>
    : <img src={institute.image_url} alt={institute.name} style={{ objectFit: fit, background: C.bgCard, ...style }} loading="lazy" />
) : (
  <div role="img" aria-label={`${institute.name} image placeholder`} style={{ display: "flex", alignItems: "center", justifyContent: "center", background: C.accentBgSubtle, color: C.accent, fontSize: iconSize, ...style }}>
    {templateIcon(institute.type)}
  </div>
);
const ALL_INSTITUTE_TYPES = [
  { value: "School", label: "School" },
  { value: "College", label: "College / University" },
  { value: "Company", label: "Company" },
  { value: "Club", label: "Sports Club" },
  { value: "Gym", label: "Gym" },
  { value: "Organisation", label: "Organisation" },
  { value: "Challenge", label: "Challenge" },
  { value: "Collective Group", label: "Collective Group" },
];
const inactiveInstitutionStatuses = ["expired", "cancelled", "revoked", "past_due", "inactive"];
const isInstitutionInactive = (status) => inactiveInstitutionStatuses.includes(String(status || "").toLowerCase());
const isInstitutionAccessLocked = (institution) => Boolean(institution && (institution.access_active === false || isInstitutionInactive(institution.billing_status)));
const institutionStepText = (locked, value) => locked ? "Locked" : Number(value || 0).toLocaleString();

/* ═══════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════ */
const makeCSS = (C) => `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; -webkit-font-smoothing: antialiased; }
html { scroll-behavior: smooth; }
body { font-family: 'DM Sans', sans-serif; background: ${C.bg}; color: ${C.text}; }
a { color: inherit; text-decoration: none; }
::-webkit-scrollbar { width: 5px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: ${C.scrollbarThumb}; border-radius: 99px; }
.heading { font-family: 'Sora', sans-serif; }
.grad-text { background: linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
.theme-toggle { transition: transform 0.2s ease; }
.theme-toggle:hover { transform: rotate(15deg) scale(1.1); }
.card-lift { transition: transform 0.3s cubic-bezier(0.16,1,0.3,1), box-shadow 0.3s ease; }
.card-lift:hover { transform: translateY(-3px); box-shadow: ${C.shadowLg}; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
.fade-in { animation: fadeIn 0.3s cubic-bezier(0.16,1,0.3,1); }

.hide-mobile { display: block; }
.show-mobile { display: none; }

@media (max-width: 768px) {
  .hide-mobile { display: none !important; }
  .show-mobile { display: block !important; }
  .site-header-center { display: none !important; }
}
`;

/* ═══════════════════════════════════════════
   SHARED STYLE HELPERS
═══════════════════════════════════════════ */
const card = (C, extra = {}) => ({
  background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, boxShadow: C.shadowMd, ...extra,
});

/* ═══════════════════════════════════════════
   PROGRESS BAR
═══════════════════════════════════════════ */
const Bar = ({ C, pct }) => (
  <div style={{ height: 6, borderRadius: 99, background: C.bgAlt, border: `1px solid ${C.border}`, overflow: "hidden" }}>
    <div style={{ height: "100%", width: `${pct}%`, background: C.accentGrad, borderRadius: 99, transition: "width 0.6s ease" }} />
  </div>
);

/* ═══════════════════════════════════════════
   RANK BADGE
═══════════════════════════════════════════ */
const RankBadge = ({ C, rank }) => {
  const colors = {
    1: { bg: "rgba(212,160,23,0.12)", border: "rgba(212,160,23,0.35)", color: "#D4A017" },
    2: { bg: "rgba(160,174,192,0.12)", border: "rgba(160,174,192,0.35)", color: "#A0AEC0" },
    3: { bg: "rgba(201,123,58,0.12)", border: "rgba(201,123,58,0.35)", color: "#C97B3A" },
  };
  const c = colors[rank] || { bg: C.bgAlt, border: C.border, color: C.textMuted };
  return (
    <div style={{ width: 32, height: 32, borderRadius: 10, background: c.bg, border: `1px solid ${c.border}`, color: c.color, fontWeight: 800, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: "'Sora', sans-serif" }}>
      {rank}
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const InstituteLeaderboard = () => {
  const { user } = useAuth();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;
  const isCompact = windowWidth < 900;

  const [isDark, setIsDark] = useState(() => { try { return localStorage.getItem("spa-theme") !== "light"; } catch { return true; } });
  const C = isDark ? DARK : LIGHT;
  const toggleTheme = useCallback(() => { setIsDark(v => { const next = !v; try { localStorage.setItem("spa-theme", next ? "dark" : "light"); } catch {} return next; }); }, []);

  const [scope, setScope] = useState("national");
  const [country, setCountry] = useState("Ireland");
  const [region, setRegion] = useState("Dublin");
  const [peerBoard, setPeerBoard] = useState([]);
  const [loadingRanks, setLoadingRanks] = useState(false);
  const [instituteType, setInstituteType] = useState("School");
  const isSchoolLeaderboard = instituteType === "School";

  /* ── RANKINGS ── */
  useEffect(() => {
    const loadRankings = async () => {
      setLoadingRanks(true);
      try {
        const t = instituteType || "School";
        if (isDemoCollegeLeaderboard(user?.email, t)) {
          const { data: collegeRows, error: collegeError } = await supabase.rpc('get_demo_college_leaderboard', {
            p_scope: scope, p_country: country, p_state_province: region,
          });
          if (collegeError) throw collegeError;
          setPeerBoard(buildDemoCollegeLeaderboard({ realRows: collegeRows || [] }));
          return;
        }
        const { data: insts, error: instErr } = await supabase.rpc('get_public_institute_leaderboard', {
          p_institute_type: t, p_scope: scope, p_country: country, p_state_province: region,
        });
        if (instErr) throw instErr;
        if (!insts?.length) { setPeerBoard([]); return; }
        const publicInstitutes = insts.filter((row) => !isDemoCompanyInstituteRow(row));
        if (!publicInstitutes.length) { setPeerBoard([]); return; }
        const displayInstitutes = publicInstitutes;
        const rows = displayInstitutes
          .map((i) => {
            const accessLocked = isInstitutionAccessLocked(i);
            return {
              id: i.id,
              name: i.name?.match(/Students$/i) ? i.name.replace(/Students$/i, "").trim() : i.name,
              type: t,
              institute_type: t,
              region: i.state_province,
              country: i.country,
              image_url: i.image_url,
              totalSteps: Number(i.total_steps) || 0,
              billing_status: i.billing_status,
              access_active: i.access_active,
              accessLocked,
            };
          })
          .sort((a, b) => {
            if (a.accessLocked !== b.accessLocked) return a.accessLocked ? 1 : -1;
            if (a.accessLocked) return a.name.localeCompare(b.name);
            return b.totalSteps - a.totalSteps || a.name.localeCompare(b.name);
          })
          .map((row, idx) => ({ ...row, rank: idx + 1 }));
        setPeerBoard(rows);
      } catch (e) { console.error("loadRankings error:", e); setPeerBoard([]); } finally { setLoadingRanks(false); }
    };
    loadRankings();
  }, [instituteType, scope, country, region, user?.email]);

  const effectiveType = instituteType || "Institute";
  const maxPeerSteps = useMemo(() => {
    const unlockedSteps = peerBoard.filter(i => !i.accessLocked).map(i => i.totalSteps);
    return unlockedSteps.length ? Math.max(...unlockedSteps) : 0;
  }, [peerBoard]);
  const filterLabel = {
    display: "block",
    fontSize: 11,
    fontWeight: 800,
    color: C.textMuted,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 7,
  };
  const filterInput = {
    width: "100%",
    minHeight: 42,
    padding: "9px 11px",
    borderRadius: 10,
    background: C.inputBg,
    border: `1px solid ${C.inputBorder}`,
    color: C.inputText,
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
  };
  const scopeOptions = ["regional", "national", "global"];

  // const noImage = "https://wcislnchclarifyudagt.supabase.co/storage/v1/object/public/images/app-Images/no-image.jpg";

  /* ─── RENDER ─── */
  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'DM Sans', sans-serif", transition: "background 0.3s ease, color 0.3s ease" }}>
      <style>{makeCSS(C)}</style>

      {/* ══ HEADER ══ */}
      <header style={{ position: "sticky", top: 0, zIndex: 1000, height: 56, padding: "0 16px", display: "flex", alignItems: "center", justifyContent: "space-between", background: C.headerBg, backdropFilter: "blur(20px) saturate(1.2)", WebkitBackdropFilter: "blur(20px) saturate(1.2)", borderBottom: `1px solid ${C.border}` }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: C.bgElevated, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <img src={logo} alt="Snows ProAm" style={{ width: 18, height: 18, objectFit: "contain" }} />
          </div>
          <span style={{ fontFamily: "'Sora', sans-serif", fontSize: 14, fontWeight: 700, color: C.text, whiteSpace: "nowrap" }}>
            ProAm <span className="grad-text">Institutes</span>
          </span>
        </Link>
        <div className="site-header-center" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, fontSize: 10, fontWeight: 800, color: C.accent, letterSpacing: 0.4, textTransform: "uppercase", pointerEvents: "none" }}>
          Institute Leaderboard
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button className="theme-toggle" aria-label={isDark ? "Use light theme" : "Use dark theme"} onClick={toggleTheme} style={{ width: 32, height: 32, borderRadius: 8, background: C.bgElevated, border: `1px solid ${C.border}`, color: C.text, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <FontAwesomeIcon icon={isDark ? faSun : faMoon} style={{ fontSize: 12 }} />
          </button>
        </div>
      </header>

      <div style={{ width: "100%", maxWidth: isSchoolLeaderboard ? "none" : 1200, margin: "0 auto", padding: isMobile ? "20px 12px 80px" : "28px 20px 80px" }}>

        {/* ══ PUBLIC FILTERS ══ */}
        <div style={{ ...card(C, { padding: isMobile ? 16 : 18, marginBottom: 16, borderRadius: 18 }) }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>Explore</div>
              <div className="heading" style={{ fontSize: isMobile ? 17 : 19, fontWeight: 800, color: C.text, letterSpacing: -0.2 }}>Institute rankings</div>
            </div>
            <div style={{ padding: "6px 10px", borderRadius: 999, background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, color: C.accent, fontSize: 12, fontWeight: 800, whiteSpace: "nowrap", textTransform: "capitalize" }}>
              {scope}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "minmax(180px, 0.8fr) minmax(280px, 1.1fr) minmax(320px, 1.2fr)", gap: 14, alignItems: "end" }}>
            <label>
              <span style={filterLabel}>Institution type</span>
              <select value={instituteType} onChange={e => setInstituteType(e.target.value)} style={{ ...filterInput, WebkitAppearance: "none", appearance: "none" }}>
                {ALL_INSTITUTE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>

            <div>
              <div style={filterLabel}>Scope</div>
              <div role="group" aria-label="Leaderboard scope" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4, background: C.bgAlt, border: `1px solid ${C.border}`, padding: 4, borderRadius: 12, minHeight: 42 }}>
                {scopeOptions.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setScope(s)}
                    aria-pressed={scope === s}
                    style={{ border: "none", background: scope === s ? C.accentGrad : "transparent", color: scope === s ? "#fff" : C.textSecondary, fontWeight: 800, fontSize: 12, padding: "9px 10px", borderRadius: 9, cursor: "pointer", textTransform: "capitalize", fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div style={filterLabel}>Location</div>
              <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr", gap: 8 }}>
                <label>
                  <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>Country</span>
                  <input
                    value={country}
                    onChange={e => setCountry(e.target.value)}
                    placeholder="Country"
                    disabled={scope === "global"}
                    style={{ ...filterInput, opacity: scope === "global" ? 0.55 : 1, cursor: scope === "global" ? "not-allowed" : "text" }}
                  />
                </label>
                <label>
                  <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>Region</span>
                  <input
                    value={region}
                    onChange={e => setRegion(e.target.value)}
                    placeholder={scope === "national" ? "Region not needed" : "Region"}
                    disabled={scope !== "regional"}
                    style={{ ...filterInput, opacity: scope === "regional" ? 1 : 0.55, cursor: scope === "regional" ? "text" : "not-allowed" }}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* ══ RANKINGS PANEL ══ */}
        <div style={{ ...card(C, { padding: isMobile ? 16 : 24, borderRadius: 20 }) }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.accent, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>Rankings</div>
              <h2 className="heading" style={{ fontSize: isMobile ? 16 : 18, fontWeight: 800, color: C.text }}>
                {effectiveType} <span className="grad-text">Leaderboard</span>
              </h2>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: C.textMuted }}>
                {scope === "global" ? "🌍 Global" : scope === "national" ? `${country === "Ireland" ? "🇮🇪" : "🇬🇧"} ${country}` : `📍 ${region}, ${country}`}
              </span>
            </div>
          </div>

          {loadingRanks ? (
            <div style={{ textAlign: "center", padding: "28px 0", color: C.textMuted }}>⏳ Loading rankings…</div>
          ) : peerBoard.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {peerBoard.map(inst => {
                const useWorkspaceLogo = ['company', 'club', 'school', 'college'].includes(String(inst.institute_type || inst.type || '').toLowerCase());
                return (
                <div key={inst.id} style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 14, padding: isMobile ? "10px 12px" : "12px 16px", borderRadius: 12, background: C.bgAlt, border: `1px solid ${C.border}` }}>
                  <RankBadge C={C} rank={inst.rank} />
                  <InstituteImage
                    C={C}
                    institute={inst}
                    iconSize={isMobile ? 18 : 22}
                    fit={useWorkspaceLogo ? "contain" : "cover"}
                    style={useWorkspaceLogo ? {
                      width: isMobile ? 52 : 60,
                      height: isMobile ? 52 : 60,
                      borderRadius: 10,
                      border: `1px solid ${C.border}`,
                      padding: 6,
                      flexShrink: 0,
                    } : {
                      width: isMobile ? 96 : 140,
                      height: isMobile ? 48 : 60,
                      borderRadius: 10,
                      border: `1px solid ${C.border}`,
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: C.text, fontSize: isMobile ? 13 : 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inst.name}</div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>{inst.region ? `${inst.region}, ` : ""}{inst.country}</div>
                    <Bar C={C} pct={inst.accessLocked ? 0 : maxPeerSteps ? Math.round((inst.totalSteps / maxPeerSteps) * 100) : 0} />
                  </div>
                  <div className="heading" style={{ fontSize: isMobile ? 13 : 15, fontWeight: 700, color: C.accent, flexShrink: 0, textAlign: "right" }}>
                    {institutionStepText(inst.accessLocked, inst.totalSteps)}
                    <div style={{ fontSize: 10, color: C.textMuted, fontWeight: 500 }}>{inst.accessLocked ? "hidden" : "steps"}</div>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "28px 0" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🏆</div>
              <p style={{ color: C.textMuted, fontSize: 13 }}>No {effectiveType.toLowerCase()}s found for this region yet.</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default InstituteLeaderboard;
